import { ChangeEvent, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormInput,
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules';
import {
  ReportDetailsVm,
  reportDetailsVm,
  reporterNotificationEmailUpdated,
} from 'src/components/pages/Case/Case.slice';
import { hiddenDots } from 'src/components/pages/Case/helpers/hiddenDots';
import { ReportType, useUpdateContactEmailMutation } from 'src/graphql';
import { FormInputVariant, FormState } from 'src/types';
import { ReporterDetailsInputFieldContainerStyled } from '../NewReporterDetails.styles';

interface NotificationEmailInputFieldProps {
  reportId: ReportType['id'];
  testVm?: ReportDetailsVm;
}

export const NotificationEmailInputField = ({
  reportId,
  testVm,
}: NotificationEmailInputFieldProps) => {
  const dispatch = useDispatch();
  const { notificationEmailState, reporter } = useSelector(
    reportDetailsVm({ reportId, testVm }),
  );

  const queryClient = useQueryClient();

  const { mutate: mutationUpdateContactEmail } = useUpdateContactEmailMutation({
    onSuccess: async () => {
      handleSuccessToast({
        toastMessage: 'Notification email updated successfully.',
      });
      if (isMasked) {
        setContactEmail(hiddenDots);
      }
      setNotificationEmailInputState(FormState.DEFAULT);
      await queryClient.invalidateQueries(['CaseById']);
    },
    onError: () => {
      handleErrorToast({
        toastMessage: 'Error updating notification email.',
      });
    },
  });

  const [notificationEmailInputState, setNotificationEmailInputState] =
    useState<FormState>(FormState.DEFAULT);

  const isMasked = reporter?.contactEmail === hiddenDots;
  const [contactEmail, setContactEmail] = useState<string>(
    reporter?.contactEmail ?? '',
  );
  const [editingMasked, setEditingMasked] = useState(false);

  const handleEmailOnBlur = () => {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail);
    if (
      (isMasked && editingMasked && isValid && contactEmail !== '') ||
      (reporter?.id &&
        isValid &&
        contactEmail !== (reporter?.contactEmail ?? ''))
    ) {
      mutationUpdateContactEmail({
        contactEmail,
        userId: reporter?.id,
        reportId,
      });
      dispatch(reporterNotificationEmailUpdated({ reportId, contactEmail }));
      setEditingMasked(false);
    }
    if (isMasked && editingMasked) {
      setContactEmail(hiddenDots);
      setEditingMasked(false);
      setNotificationEmailInputState(notificationEmailState);
      return;
    }
    setNotificationEmailInputState(notificationEmailState);
  };

  const handleEmailOnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value = e.target.value;
    if (isMasked && contactEmail === hiddenDots && !editingMasked) {
      value = '';
      setEditingMasked(true);
    }
    setContactEmail(value);
    setNotificationEmailInputState(
      value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? FormState.ERROR
        : FormState.DEFAULT,
    );
  };

  return (
    <ReporterDetailsInputFieldContainerStyled>
      <FormInput
        isRequired={false}
        label="Notification email"
        data-testid="notification-email-form-input"
        variant={FormInputVariant.INPUT}
        state={notificationEmailInputState}
        value={contactEmail ?? reporter?.contactEmail}
        placeholder={'Example@example.com'}
        onBlur={handleEmailOnBlur}
        onChange={handleEmailOnChange}
        helpText="Please enter a valid email address."
      />
    </ReporterDetailsInputFieldContainerStyled>
  );
};
